/* global RequestIdleCallbackHandle */
import React, { FunctionComponent, useCallback, useEffect, useRef, useLayoutEffect, useState } from 'react';
import { useAnchorScrollingState } from 'app/components/common/AnchorScrollingContext';
import { useScrollCtrl, useScrollContextState } from 'app/components/scroll/ScrollContext';
import { useStickyHeight } from 'app/components/layout/Page/StickyContext';
import { createStorageHook } from 'app/components/storage/createStorageHook';
import { CallStateConsumer, CallStage } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { StorageType } from 'app/utils/StorageContext';
import { getOrCreateLocationKey } from 'app/utils/env';
import usePrevious from '@engoo/react-hooks/usePrevious';
import { getOrElse } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { is } from 'immutable';
import * as t from 'io-ts';
import throttle from 'lodash/throttle';


interface IProps {
  readonly autoSave?: boolean;
  readonly requestIdleCallbackImpl?: typeof requestIdleCallback;
  readonly getOrCreateLocationKeyImpl?: typeof getOrCreateLocationKey;
}

interface IDeps {
  readonly callStage?: CallStage;
}

export const Position = t.interface({
  x: t.number,
  y: t.number,
});

type IPosition = t.TypeOf<typeof Position>;

const scrollPositionsParser = {
  serialize: (value: {[locationKey: string]: {[id: string]: IPosition}}) => value,
  deserialize: (stored: unknown) =>
    pipe(
      t.record(t.string, t.record(t.string, Position)).decode(stored),
      getOrElse(() => ({})),
    ),
  isEqual: is,
};

export const SCROLL_POSITIONS_KEY = 'SCROLL_POSITIONS';

const useScrollPositions = createStorageHook<{[locationKey: string]: {[id: string]: IPosition}}>(
  SCROLL_POSITIONS_KEY,
  scrollPositionsParser,
  StorageType.SESSION,
);

/**
 * `ScrollPositioner` will try to restore the saved scroll position,
 * scroll to the url anchor or scroll to the main content (if in a video call).
 *
 * It uses the scroller provided by the `ScrollContext`, which also provides
 * unique ids for each scroller.
 *
 * For dynamically-loaded content we need to make sure that this
 * component is mounted together with the content.
 *
 * How Scrolling on Mount Works:
 *
 * Scrolling on mount is complicated by the fact that not all information is available at first. Before
 * we can scroll, we need to wait for two pieces of information: 1) if there is an active booking, we need
 * to wait for the video to be loaded so that we can scroll past it, and 2) the sticky header does not
 * have the correct height from the start, so we need to wait for its height to be updated.
 *
 * There are three main possible flows:
 *
 * Case 1: There is a saved position or anchor to scroll to. We postpone the scroll until one of two things
 * happens: either an updated sticky header height arrives or render finishes and the idle callback is
 * executed.
 * useLayoutEffect => postponeScroll =>
 * requestIdleCallback => runScrollOnStickyUpdate => scrollOnStickyUpdate
 *                  OR => sticky height useEffect => runScrollOnStickyUpdate => scrollOnStickyUpdate
 *
 * Case 2: There is a booking state. We have to wait for the video to be lazy loaded. Once the video is
 * loaded, if we are on the correct call stage, we postpone the scroll again in case we still need to wait
 * for an updated sticky header height.
 * useLayoutEffect => waitForLazyLoad = true => lazyLoaded useLayoutEffect => scrollToContent =>
 * postponeScroll => (Same as Case 1)
 *
 * Case 3: If none of the previous two cases happens, we scroll to the top.
 */
export const ScrollPositioner: FunctionComponent<IProps & IDeps> = ({
  callStage = CallStage.Unknown,
  autoSave,
  requestIdleCallbackImpl = requestIdleCallback,
  getOrCreateLocationKeyImpl = getOrCreateLocationKey,
}) => {
  const scroll = useScrollCtrl();
  const anchorScrollingState = useAnchorScrollingState();
  const bookingState = useBookingState();
  const stickyHeight = useStickyHeight();
  const initialScrollingDone = useRef(false);
  const waitForLazyLoad = useRef(false);
  const scrollOnStickyUpdate = useRef<(updatedStickyHeight: number) => void>();
  const scrollIdleCallback = useRef<RequestIdleCallbackHandle>(0);
  const lazyLoaded = useScrollContextState().videoLazyLoaded;
  // used to keep track of changes in current scroll position
  const [scrollPosition, setScrollPosition] = useState<IPosition>();
  // scroll positions saved to session storage
  const [savedScrollPositions, setSavedScrollPositions] = useScrollPositions();

  /**
   * Scrolling priority when initializing this componenet:
   * 1) If there is a saved position, scroll there.
   * 2) If the url contains an anchor, scroll to the anchor.
   * 3) If we are in a video call and there is a content section, scroll to content.
   * 4) Scroll to top.
   */
  useLayoutEffect(() => {
    const position = ((savedScrollPositions || {})[getOrCreateLocationKeyImpl()] || {})[scroll.scrollId];
    if (position && autoSave) {
      postponeScroll(() => scroll.scrollTo(position));
    } else if (anchorScrollingState && anchorScrollingState.anchor) {
      postponeScroll((updatedStickyHeight: number) =>
        scroll.scrollToAnchor(anchorScrollingState.anchor, true, -updatedStickyHeight));
    } else if (bookingState) {
      waitForLazyLoad.current = true;
    } else {
      postponeScroll(() => scrollToTop());
    }

    return () => {
      cancelIdleCallback(scrollIdleCallback.current);
    };
    // Providing empty array because this code is only run once "on mount"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to content once the video in the header has been loaded.
  useLayoutEffect(() => {
    if (lazyLoaded &&
        !initialScrollingDone.current &&
        waitForLazyLoad.current) {
      if (callStage !== CallStage.PostLocked &&
          callStage !== CallStage.Unknown) {
        scrollToContent();
      } else if (callStage === CallStage.PostLocked) {
        scrollToTop();
      }
    }
    // We only care about running this once the video has been loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyLoaded, callStage]);

  // Runs the postponed scroll and resets the ref to the scrolling method.
  const runScrollOnStickyUpdate = useCallback(() => {
    if (scrollOnStickyUpdate.current) {
      scrollOnStickyUpdate.current(stickyHeight);
    }
    scrollOnStickyUpdate.current = undefined;
    initialScrollingDone.current = true;
    waitForLazyLoad.current = false;
  }, [scrollOnStickyUpdate, stickyHeight]);

  // Delayed scrolling due to the sticky height changing after initial render.
  const prevStickyHeight = usePrevious(stickyHeight);
  useEffect(() => {
    if (prevStickyHeight !== undefined && prevStickyHeight !== stickyHeight && scrollOnStickyUpdate.current) {
      runScrollOnStickyUpdate();
    }
  }, [stickyHeight, prevStickyHeight, runScrollOnStickyUpdate]);

  /**
   * Postpones a scrolling action to be run after the sticky height is updated. If the sticky height is not
   * updated after render (if we reach the code inside the idle callback), we scroll anyway.
   * @param scrollFunction
   */
  function postponeScroll(scrollFunction: (updatedStickyHeight: number) => void) {
    scrollOnStickyUpdate.current = scrollFunction;
    scrollIdleCallback.current = requestIdleCallbackImpl(() => {
      // This method will only run the scroll function if a sticky header change has not triggered the scroll yet.
      runScrollOnStickyUpdate();
    });
  }

  function scrollToContent() {
    postponeScroll((updatedStickyHeight: number) => {
      if (!scroll.scrollToAnchor('content', false, -updatedStickyHeight)) {
        scrollToTop();
      }
    });
  }

  function scrollToTop() {
    scroll.scrollTo({ x: 0, y: 0 });
    initialScrollingDone.current = true;
    waitForLazyLoad.current = false;
  }

  /**
   * Set up throttled scroll position saving.
   */
  useEffect(() => {
    const throttled = throttle(() => {
      setScrollPosition(scroll.getPosition());
    }, 1000);
    const eventMap = {
      scroll: () => throttled(),
    };
    scroll.bindEvents(eventMap);

    return () => {
      scroll.unbindEvents(eventMap);
      throttled.cancel();
    };
  }, [scroll]);

  // Whenever a scroll event happens, we save the scroll position.
  const prevScrollPosition = usePrevious(scrollPosition);
  useEffect(() => {
    if (autoSave && scrollPosition && initialScrollingDone.current
        && (!prevScrollPosition || prevScrollPosition !== scrollPosition)) {
      setSavedScrollPositions({
        ...savedScrollPositions,
        [getOrCreateLocationKeyImpl()]: {
          ...savedScrollPositions ? savedScrollPositions[getOrCreateLocationKeyImpl()] : {},
          [scroll.scrollId]: { ...scrollPosition },
        },
      });
    }
  }, [
    scrollPosition,
    savedScrollPositions,
    setSavedScrollPositions,
    prevScrollPosition,
    scroll,
    autoSave,
    getOrCreateLocationKeyImpl,
  ]);

  // If anchorChangeCounter changes, scroll to anchor (usually happens after a local
  // or remote anchor click or when coordinating scrolling between editor and preview panes).
  // The reason we use a counter to keep track of when we need to scroll to an anchor,
  // instead of just listening to changes in the history or location key is because
  // there are times when those change, but we don't want to scroll to the anchor, such as
  // when starting a video call.
  const prevState = usePrevious(anchorScrollingState);
  useEffect(() => {
    if (prevState && anchorScrollingState && initialScrollingDone.current
      && prevState.anchorChangeCounter !== anchorScrollingState.anchorChangeCounter) {
      scroll.scrollToAnchor(anchorScrollingState.anchor, true, -stickyHeight);
    }
    // scroll only happens if the anchorScrollingState changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorScrollingState]);

  return null;
};

const ScrollPositionerInjectDependencies: FunctionComponent<IProps> = (props) =>
  <CallStateConsumer>{({ stage }) =>
    <ScrollPositioner
      {...props}
      callStage={stage}
    />
  }</CallStateConsumer>;

export default ScrollPositionerInjectDependencies;
